import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { IoMdPrint } from "react-icons/io";
import { Calendar } from "react-calendar";
import { Info } from "lucide-react";
import "react-calendar/dist/Calendar.css";
import StepsIcon from "../assets/StepsIcon.png";
import CaloriesIcon from "../assets/CaloriesIcon.png";
import ProgressCircleright from "./ProgressCircleright";
import ProgressLinear from "./ProgressLinear";
import Graph from "./Graph";
import VerticalGraph from "./VerticalGraph";
import LineGraph from "./LineGraph";
import ProgressCircle from "./ProgressCircle";
import { toApiDate, toApiDate1, isDateRangeValid } from "../utils/DateUtils";
import "./styles/MeDashboard.css";
import getNiceYAxisMax from "../../components/utils/getNiceAxisMax";
import SkeletonLoader from "../Loaders/SkeletonLoader";
import openNotification from "../utils/CustomNotification";
import moment from "moment-timezone";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";
import { SessionExtend } from "components/utils/SessionCheckUtils";
import { useNavigate } from "react-router-dom";
import Streaktrends from "./Streaktrends"
import ActivityStatCards from "./ActivityStatCard";
import { RiPinDistanceFill, RiFireLine } from "react-icons/ri";
import { IoFootsteps } from "react-icons/io5";

type Value = Date | null | [Date | null, Date | null];

const API_URL_BODYMETRICS = `${process.env.REACT_APP_API_URL}/api/get-individual-body-metrics`;
const API_URL_METRICS = `${process.env.REACT_APP_API_URL}/api/get-individual-metrics`;
const sessionUid = localStorage.getItem("session_uid") ?? "";
const userUid = localStorage.getItem("user_uid") ?? "";
const userTimeZone = "Asia/Kolkata";

const MeDashboard: React.FC = () => {
  useCheckSessionHeartBeat();
  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 6);
  const [loading, setLoading] = useState(true);

  const [selectedDate, setSelectedDate] = useState<[Date | null, Date | null]>([
    sevenDaysAgo,
    today,
  ]);

  const [sessionUid, setSessionUid] = useState("");
  const [userUid, setUserUid] = useState("");
  const [calenderDate, setCalenderDate] = useState<Date>(new Date());
  const [showActivityTooltip, setShowActivityTooltip] = useState(false);
  const [showBodyMetricsTooltip, setShowBodyMetricsTooltip] = useState(false);
  const [showGoalCompletionTooltip, setShowGoalCompletionTooltip] =
    useState(false);
  const [showOverallPerformanceTooltip, setShowOverallPerformanceTooltip] =
    useState(false);
  const [selectedBodyMetric, setSelectedBodyMetric] = useState("Weight");
  const [rawBodyMetrics, setRawBodyMetrics] = useState<any[]>([]);
  const [yAxisMax, setYAxisMax] = useState<number | undefined>(undefined);
  const [selectedActivityMetric, setSelectedActivityMetric] = useState("Steps");
  const [rawActivityMetrics, setActivityMetrics] = useState<any[]>([]);
  const [performanceData, setPerformanceData] = useState<
    { date: string; overall_performance: number }[]
  >([]);

  const [metricSummary, setMetricSummary] = useState<{
    steps?: { avg: number; min: number; max: number };
    distance?: { avg: number; min: number; max: number };
    caloriesburned?: { avg: number; min: number; max: number };
  }>({});

  const [graphData, setGraphData] = useState<{
    target: number[];
    achieved: number[];
    labels: string[];
  }>({
    target: [],
    achieved: [],
    labels: [],
  });
  const [bodyMetricsGraphData, setBodyMetricsGraphData] = useState<{
    series: { name: string; data: number[] }[];
    categories: string[];
  }>({
    series: [],
    categories: [],
  });
  const [goalCompletion, setGoalCompletion] = useState({
    steps: 0,
    stepsTarget: 0,
    stepsValue: 0,
    calories: 0,
    caloriesTarget: 0,
    caloriesValue: 0,
    distance: 0,
    distanceTarget: 0,
    distanceValue: 0,
  });

  const [rightMetrics, setRightMetrics] = useState({
    steps: 0,
    stepsTarget: 0,
    stepsValue: 0,
    stepsUnit: "steps",
    caloriesTarget: 0,
    caloriesValue: 0,
    caloriesUnit: "kcal",
    distanceTarget: 0,
    distanceValue: 0,
    distanceUnit: "km",
  });

  const [bodyUnits, setBodyUnits] = useState<{
    height: string;
    weight: string;
    chest: string;
    waist: string;
  }>({
    height: "cm",
    weight: "kg",
    chest: "cm",
    waist: "cm",
  });

  useEffect(() => {
    const sid = localStorage.getItem("session_uid") ?? "";
    const uid = localStorage.getItem("user_uid") ?? "";
    setSessionUid(sid);
    setUserUid(uid);
  }, []);

  useEffect(() => {
    if (selectedDate[0] && selectedDate[1] && sessionUid && userUid) {
      const isValid = isDateRangeValid(selectedDate[0], selectedDate[1]);

      if (!isValid) {
        openNotification({
          type: "warning",
          message: "Please select a date range of 15 days or fewer.",
        });
        return;
      }
      const start = moment(selectedDate[0]).startOf("day");
      const end = moment(selectedDate[1]).startOf("day");

      if (start.isAfter(end)) {
        openNotification({
          type: "error",
          message: "End date cannot be earlier than start date.",
        });
        return;
      }

      fetchMetrics(selectedDate[0], selectedDate[1]);
      fetchBodyMetrics(selectedDate[0], selectedDate[1]);
    }
  }, [selectedDate, sessionUid, userUid]);

  useEffect(() => {
    if (sessionUid && userUid && calenderDate) {
      fetchRightMetrics(calenderDate);
    }
  }, [sessionUid, userUid, calenderDate]);

  useEffect(() => {
    if (rawBodyMetrics.length > 0 && selectedBodyMetric) {
      processBodyGraphData(rawBodyMetrics, selectedBodyMetric);
    }
  }, [selectedBodyMetric, rawBodyMetrics]);

  useEffect(() => {
    // console.log("Processing data for metric:", selectedActivityMetric);
    if (rawActivityMetrics.length > 0 && selectedActivityMetric) {
      processGraphData(rawActivityMetrics, selectedActivityMetric);
    }
  }, [selectedActivityMetric]); // Dependency array

  const [metricUnits, setMetricUnits] = useState({
    steps: "count", // fallback default
    distance: "km",
    caloriesburned: "kcal",
  });

  const fetchMetrics = async (fromDate: Date, toDate: Date) => {
    try {
      const response = await axios.get(API_URL_METRICS, {
        params: {
          from_datetime: moment(fromDate).tz(userTimeZone).format("YYYY-MM-DDTHH:mm:ss"),
          to_datetime: moment(toDate).tz(userTimeZone).format("YYYY-MM-DDTHH:mm:ss"),
          metric_data_type: "all",
          goal_type: "all",
        },
        headers: {
          "user-uid": userUid,
          accept: "application/json",
        },
        withCredentials: true,
      });

      console.log("API response:", response.data); // <-- log full API response

      if (response.data.status === "Success") {
        const data = response.data.data;

        setLoading(false);
        setActivityMetrics(data);

        processGraphData(data, selectedActivityMetric);
        processGoalCompletion(data);

        const summaryMetricsvalues = data?.summary_data?.metrics?.[0];
        console.log("ncjendc", summaryMetricsvalues);
        if (summaryMetricsvalues) {
          console.log(
            "summary_data.metrics[0] snapshot:",
            JSON.stringify(summaryMetricsvalues, null, 2)
          );
          const metricSummaryObj = {
            steps: summaryMetricsvalues.steps
              ? {
                avg: summaryMetricsvalues.steps.avg_value ?? 0,
                min: summaryMetricsvalues.steps.min_value ?? 0,
                max: summaryMetricsvalues.steps.max_value ?? 0,
              }
              : undefined,
            distance: summaryMetricsvalues.distance
              ? {
                avg: summaryMetricsvalues.distance.avg_value ?? 0,
                min: summaryMetricsvalues.distance.min_value ?? 0,
                max: summaryMetricsvalues.distance.max_value ?? 0,
              }
              : undefined,
            caloriesburned: summaryMetricsvalues.caloriesburned
              ? {
                avg: summaryMetricsvalues.caloriesburned.avg_value ?? 0,
                min: summaryMetricsvalues.caloriesburned.min_value ?? 0,
                max: summaryMetricsvalues.caloriesburned.max_value ?? 0,
              }
              : undefined,
          };


          setMetricSummary(metricSummaryObj);
        }

        setPerformanceData(
          data.data.map((entry: any) => ({
            date: entry.date,
            overall_performance: entry.overall_performance || 0,
          }))
        );

        const firstDayMetrics = data?.data?.[0]?.metrics?.[0];
        if (firstDayMetrics) {
          setMetricUnits({
            steps: firstDayMetrics.steps?.unit || "count",
            distance: firstDayMetrics.distance?.unit || "km",
            caloriesburned: firstDayMetrics.caloriesburned?.unit || "kcal",
          });
        }
      }

      SessionExtend();
    } catch (error) {
      console.error("Error fetching metrics:", error);
    }
  };

  const fetchRightMetrics = async (date: Date) => {
    try {
      const fromDateright = moment(date)
        .tz(userTimeZone)
        .format("YYYY-MM-DDTHH:mm:ss");
      const toDateright = fromDateright;

      const response = await axios.get(API_URL_METRICS, {
        params: {
          from_datetime: fromDateright,
          to_datetime: toDateright,
          metric_data_type: "all",
          goal_type: "all",
        },
        headers: {
          "user-uid": userUid,
          accept: "application/json",
        },
        withCredentials: true,
      });

      if (response.data.status === "Success") {
        processRightPanelData(response.data.data);
      }
      SessionExtend();
    } catch (error) {
      console.error("Error fetching right panel metrics:", error);
    }
  };

  // const fetchBodyMetrics = async (fromDate: Date, toDate: Date) => {
  //   const fromDateStr = fromDate.toISOString().split("T")[0] + "T00:00:00Z";
  //   const toDateStr = toDate.toISOString().split("T")[0] + "T00:00:00Z";
  //   try {
  //     const response = await axios.get(API_URL_BODYMETRICS, {
  //       params: { from_datetime: fromDateStr, to_datetime: toDateStr },
  //       headers: {
  //         "session-uid": sessionUid,
  //         "user-uid": userUid,
  //         accept: "application/json",
  //       },
  //     });

  //     if (response.data.status === "Success") {
  //       setRawBodyMetrics(response.data.data.data); // Only store raw
  //     }
  //      SessionExtend();
  //   } catch (error) {
  //     console.error("Error fetching body metrics:", error);
  //   }
  // };

  //   const fetchBodyMetrics = async (fromDate: Date, toDate: Date) => {
  //   const fromDateStr = fromDate.toISOString().split("T")[0] + "T00:00:00Z";
  //   const toDateStr = toDate.toISOString().split("T")[0] + "T00:00:00Z";
  //   try {
  //     const response = await axios.get(API_URL_BODYMETRICS, {
  //       params: { from_datetime: fromDateStr, to_datetime: toDateStr },
  //       headers: {
  //         "session-uid": sessionUid,
  //         "user-uid": userUid,
  //         accept: "application/json",
  //       },
  //     });

  //     if (response.data.status === "Success") {
  //       const bodyData = response.data.data.data || [];
  //       setRawBodyMetrics(bodyData);

  //       // Dynamically extract units from the first non-null entry
  //       if (bodyData.length > 0) {
  //         const firstEntry = bodyData.find(
  //           (entry: any) =>
  //             entry.height_unit || entry.weight_unit || entry.chest_unit || entry.waist_unit
  //         );

  //         if (firstEntry) {
  //           setBodyUnits({
  //             height: firstEntry.height_unit || "cm",
  //             weight: firstEntry.weight_unit || "kg",
  //             chest: firstEntry.chest_unit || "cm",
  //             waist: firstEntry.waist_unit || "cm",
  //           });
  //         }
  //       }
  //     }

  //     SessionExtend();
  //   } catch (error) {
  //     console.error("Error fetching body metrics:", error);
  //   }
  // };

  const fetchBodyMetrics = async (fromDate: Date, toDate: Date) => {
    const fromDateStr = fromDate.toISOString().split("T")[0] + "T00:00:00Z";
    const toDateStr = toDate.toISOString().split("T")[0] + "T00:00:00Z";
    try {
      const response = await axios.get(API_URL_BODYMETRICS, {
        params: { from_datetime: fromDateStr, to_datetime: toDateStr },
        headers: {
          "user-uid": userUid,
          accept: "application/json",
        },
        withCredentials: true,
      });

      if (response.data.status === "Success") {
        const bodyData = response.data.data.data || [];
        setRawBodyMetrics(bodyData);

        if (bodyData.length > 0) {
          const firstMetrics = bodyData[0].metrics;

          const getUnit = (key: string): string => {
            const found = firstMetrics.find((m: any) => key in m);
            return found?.unit || "";
          };

          setBodyUnits({
            height: getUnit("height") || "cm",
            weight: getUnit("weight") || "kg",
            chest: getUnit("chest_size") || "cm",
            waist: getUnit("waist_size") || "cm",
          });
        }
      }

      SessionExtend();
    } catch (error) {
      console.error("Error fetching body metrics:", error);
    }
  };

  const metricActivityKeyMap: Record<string, string> = {
    steps: "steps",
    caloriesburned: "caloriesburned",
    distance: "distance",
  };

  const metricActivityUnitMap: Record<string, string> = {
    steps: "count",
    calories: "kcal",
    distance: "km",
  };

  const processGraphData = useCallback((data: any, metric: string) => {
    const key = metricActivityKeyMap[metric.toLowerCase()];
    if (!key) {
      console.error(`Invalid metric: ${metric}`);
      return;
    }

    const labels: string[] = data.data.map(
      (entry: { date: string }, index: number) =>
        formatDateLabel(
          new Date(entry.date),
          index,
          data.data.map((e: { date: string }) => new Date(e.date))
        )
    );

    const target = data.data.map((entry: any) => {
      const dailyMetrics = entry.metrics?.find((m: any) => m.type === "daily");
      let targetValue =
        dailyMetrics && dailyMetrics[key]?.target
          ? dailyMetrics[key].target
          : 0;
      return metric === "distance" ? targetValue / 1000 : targetValue;
    });

    const achieved = data.data.map((entry: any) => {
      const dailyMetrics = entry.metrics?.find((m: any) => m.type === "daily");
      let achievedValue =
        dailyMetrics && dailyMetrics[key]?.value ? dailyMetrics[key].value : 0;
      return metric === "distance" ? achievedValue / 1000 : achievedValue;
    });

    // Scale Y-axis based on max values
    const maxTarget = Math.max(...target);
    const maxAchieved = Math.max(...achieved);
    const rawMax = Math.max(maxTarget, maxAchieved);
    const computedYAxisMax = getNiceYAxisMax(rawMax * 1);

    setGraphData({ labels, target, achieved });
    setYAxisMax(computedYAxisMax);
  }, []);
  // useEffect(() => {
  //   console.log("Processing data for metric:", selectedActivityMetric);
  //   console.log("Generated Graph Data:", graphData);
  // }, [selectedActivityMetric, graphData]);

  const processGoalCompletion = (data: any) => {
    if (
      !data ||
      !data.summary_data ||
      !data.summary_data.metrics ||
      data.summary_data.metrics.length === 0
    ) {
      // console.log("No summary data received for goal completion.");
      return;
    }

    const summaryMetrics = data.summary_data.metrics[0];

    setGoalCompletion({
      steps: summaryMetrics.steps?.gcr || 0,
      stepsTarget: summaryMetrics.steps?.goal_count || 0,
      stepsValue: summaryMetrics.steps?.achieved_goals || 0,

      calories: summaryMetrics.caloriesburned?.gcr || 0,
      caloriesTarget: summaryMetrics.caloriesburned?.goal_count || 0,
      caloriesValue: summaryMetrics.caloriesburned?.achieved_goals || 0,

      distance: summaryMetrics.distance?.gcr || 0,
      distanceTarget: summaryMetrics.distance?.goal_count || 0,
      distanceValue: summaryMetrics.distance?.achieved_goals || 0,
    });
  };

  // const processRightPanelData = (data: any) => {
  //   if (data.data.length > 0) {
  //     const rightMetrics = data.data[0].metrics.find(
  //       (m: any) => m.type === "daily"
  //     );

  //     if (rightMetrics) {
  //       setRightMetrics({
  //         steps: rightMetrics.steps?.value || 0,
  //         stepsTarget: rightMetrics.steps?.target || 1,
  //         stepsValue: rightMetrics.steps?.value || 0,
  //         caloriesValue: rightMetrics.caloriesburned?.value || 0,
  //         caloriesTarget: rightMetrics.caloriesburned?.target || 1,
  //         distanceValue: rightMetrics.distance?.value || 0,
  //         distanceTarget: rightMetrics.distance?.target || 1,
  //       });
  //     }
  //   }
  // };
  const processRightPanelData = (data: any) => {
    if (data.data.length > 0) {
      const rightMetrics = data.data[0].metrics.find(
        (m: any) => m.type === "daily"
      );

      if (rightMetrics) {
        setRightMetrics({
          steps: rightMetrics.steps?.value || 0,
          stepsTarget: rightMetrics.steps?.target || 1,
          stepsValue: rightMetrics.steps?.value || 0,
          stepsUnit: rightMetrics.steps?.unit || "steps", // ✅ dynamic unit

          caloriesValue: rightMetrics.caloriesburned?.value || 0,
          caloriesTarget: rightMetrics.caloriesburned?.target || 1,
          caloriesUnit: rightMetrics.caloriesburned?.unit || "kcal", // ✅ dynamic unit

          distanceValue: rightMetrics.distance?.value || 0,
          distanceTarget: rightMetrics.distance?.target || 1,
          distanceUnit: rightMetrics.distance?.unit || "km", // ✅ dynamic unit
        });
      }
    }
  };

  const metricKeyMap: Record<string, string> = {
    Weight: "weight",
    Height: "height",
    Waist: "waist_size",
    Chest: "chest_size",
  };

  const metricUnitMap: Record<string, string> = {
    Weight: "kg",
    Height: "cm",
    Waist: "cm",
    Chest: "cm",
  };

  const processBodyGraphData = (rawData: any[], metric: string) => {
    const key = metricKeyMap[metric];
    const dateObjects = rawData.map((entry: any) => new Date(entry.date));
    const labels = dateObjects.map((date, index) =>
      formatDateLabel(date, index, dateObjects)
    );

    const metricData = rawData.map((entry: any) => {
      const metricEntry = entry.metrics.find((m: any) => m[key]);
      return metricEntry ? parseFloat(metricEntry[key]) : null;
    });

    setBodyMetricsGraphData({
      categories: labels,
      series: [
        {
          name: `${metric} (${metricUnitMap[metric]})`,
          data: metricData.map((v: number | null) => v ?? 0),
        },
      ],
    });
  };

  const handleDateChange = (value: Value) => {
    if (value instanceof Date) {
      const correctedDate = new Date(value);
      correctedDate.setHours(0, 0, 0, 0); // Prevent timezone issues

      const today = new Date();
      today.setHours(0, 0, 0, 0); // Normalize today's date for comparison

      if (correctedDate > today) {
        openNotification({
          type: "warning",
          message:
            "Please select today or a past date. Future dates won't have data to show.",
        });
        return;
      }

      setCalenderDate(correctedDate);
      fetchRightMetrics(correctedDate);
    }
  };

  const formatDate = (date: Date | null): string =>
    date ? date.toLocaleDateString("en-GB") : "";

  const toggleTooltip = (tooltip: string) => {
    if (tooltip === "activity") {
      setShowActivityTooltip((prev) => !prev);
      setShowBodyMetricsTooltip(false);
      setShowGoalCompletionTooltip(false);
      setShowOverallPerformanceTooltip(false);
    } else if (tooltip === "bodyMetrics") {
      setShowBodyMetricsTooltip((prev) => !prev);
      setShowActivityTooltip(false);
      setShowGoalCompletionTooltip(false);
      setShowOverallPerformanceTooltip(false);
    } else if (tooltip === "goalCompletion") {
      setShowGoalCompletionTooltip((prev) => !prev);
      setShowActivityTooltip(false);
      setShowBodyMetricsTooltip(false);
      setShowOverallPerformanceTooltip(false);
    } else if (tooltip === "Overall_Performance") {
      setShowOverallPerformanceTooltip((prev) => !prev);
      setShowActivityTooltip(false);
      setShowBodyMetricsTooltip(false);
      setShowGoalCompletionTooltip(false);
    }
    setTimeout(() => {
      setShowActivityTooltip(false);
      setShowBodyMetricsTooltip(false);
      setShowGoalCompletionTooltip(false);
      setShowOverallPerformanceTooltip(false);
    }, 1000);
  };

  //Graph Utils Later need to make a sepaerate fucntion and use here
  const formatDateLabel = (
    date: Date,
    index: number,
    allDates: Date[]
  ): string => {
    const day = date.getDate();
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[date.getMonth()];

    if (index === 0) return `${month} ${day}`;

    const prevDate = allDates[index - 1];
    const isNewMonth = prevDate.getMonth() !== date.getMonth();

    return isNewMonth ? `${month} ${day}` : `${day}`;
  };

  return (
    <>
      <div className="dashboard-container">
        <div className="mid1">
          <div className="TimeBasedMetricsHeading">
            <h4>Time-based Metrics</h4>
          </div>

          <div className="date-range-wrapper">
            <div className="date-range-container">
              <div className="date-picker">
                <label>
                  START DATE
                  <input
                    type="date"
                    value={
                      selectedDate?.[0] &&
                        !isNaN(new Date(selectedDate[0]).getTime())
                        ? new Date(selectedDate[0]).toISOString().split("T")[0]
                        : ""
                    }
                    onChange={(e) =>
                      setSelectedDate([
                        new Date(e.target.value),
                        selectedDate?.[1] ?? null,
                      ])
                    }
                    max={moment().tz(userTimeZone).format("YYYY-MM-DD")}
                  />
                </label>
              </div>

              <div className="date-picker">
                <label>
                  END DATE
                  <input
                    type="date"
                    value={
                      selectedDate?.[1] &&
                        !isNaN(new Date(selectedDate[1]).getTime())
                        ? new Date(selectedDate[1]).toISOString().split("T")[0]
                        : ""
                    }

                    onChange={(e) =>
                      setSelectedDate([
                        selectedDate?.[0] ?? null,
                        new Date(e.target.value),
                      ])
                    }
                    max={moment().tz(userTimeZone).format("YYYY-MM-DD")}
                  />
                </label>
              </div>
            </div>

            {/* <button className="GenerateReportButton">
              <span>Generate Report</span>
              <IoMdPrint />
            </button> */}
          </div>
          
          <div className="headerWithInfoIcon">
            <div className="headerWithInfoIcon-title">My Streaks</div>
          </div>
          <div className="meDashboard-GraphContainer">
            <Streaktrends />
          </div>
          <div className="headerWithInfoIcon">
            <div className="headerWithInfoIcon-title">Activity Overview</div>
            <div
              className="headingInfo-icon"
              onClick={() => toggleTooltip("activity")}
            >
              <Info size={20} />
              {showActivityTooltip && (
                <div className="tooltip">
                  This graph shows Activity Overview.
                </div>
              )}
            </div>
          </div>

          {/* --- CONTAINER 1: GRAPH --- */}
          <div className="meDashboard-GraphContainer">
            {loading ? (
              <SkeletonLoader type="vertical-chart" />
            ) : (
              <Graph
                type="bar"
                series={[
                  { name: "Target", data: graphData.target },
                  { name: "Achieved", data: graphData.achieved },
                ]}
                categories={graphData.labels}
                selectedMetric={selectedActivityMetric}
                onMetricChange={(metric) => {
                  setSelectedActivityMetric(metric);
                  processGraphData(rawActivityMetrics, metric);
                }}
                yAxisMax={yAxisMax}
                units={metricUnits}
                summary={metricSummary}
              />
            )}
          </div>

          {/* --- CONTAINER 2: STAT CARDS (SEPARATE DIV) --- */}
          {/* Placed after the graph as requested */}
          <div className="meDashboard-GraphContainer" style={{ marginTop: '16px', background: 'transparent', padding: 0, boxShadow: 'none' }}>
            {loading ? (
              <SkeletonLoader type="vertical-chart" />
            ) : (
              <ActivityStatCards
                units={metricUnits}
                summary={metricSummary}
              />
            )}
          </div>

          <div className="headerWithInfoIcon">
            <div className="headerWithInfoIcon-title">Overall Performance</div>
            <div
              className="headingInfo-icon"
              onClick={() => toggleTooltip("Overall_Performance")}
            >
              <Info size={20} />
              {showOverallPerformanceTooltip && (
                <div className="tooltip">
                  This graph shows Overall Performance data.
                </div>
              )}
            </div>
          </div>
          <div className="meDashboard-GraphContainer">
            <VerticalGraph
              data={performanceData}
              loading={loading}
              formatDateLabel={formatDateLabel}
            />
          </div>

          <div className="headerWithInfoIcon">
            <div className="headerWithInfoIcon-title">Body Metrics</div>
            <div
              className="headingInfo-icon"
              onClick={() => toggleTooltip("bodyMetrics")}
            >
              <Info size={20} />
              {showBodyMetricsTooltip && (
                <div className="tooltip">
                  This graph shows Body Metrics data.
                </div>
              )}
            </div>
          </div>
          <div className="meDashboard-GraphContainer">
            {loading ? (
              <SkeletonLoader type="vertical-chart" />
            ) : (
              <LineGraph
                type="line"
                series={bodyMetricsGraphData.series}
                categories={bodyMetricsGraphData.categories}
                selectedMetric={selectedBodyMetric}
                onMetricChange={(metric) => setSelectedBodyMetric(metric)}
                bodyUnits={bodyUnits}
              />
            )}
          </div>
          <div className="headerWithInfoIcon">
            <div className="headerWithInfoIcon-title">Goal Completion Rate</div>
            <div
              className="headingInfo-icon"
              onClick={() => toggleTooltip("goalCompletion")}
            >
              <Info size={20} />
              {showGoalCompletionTooltip && (
                <div className="tooltip">
                  This graph shows Goal Completion Rate.
                </div>
              )}
            </div>
          </div>
          <div className="GoalCompletionContainer">
            <ProgressCircle
              percentage={goalCompletion.steps}
              colour="#094752"
              heading="Steps"
            />
            <ProgressCircle
              percentage={goalCompletion.distance}
              colour="#094752"
              heading="Distance"
            />
            <ProgressCircle
              percentage={goalCompletion.calories}
              colour="#094752"
              heading="Calories"
            />
          </div>
        </div>

        <div className="right1">
          <div>
            <div className="GoalsOverviewHeader">Goals Overview Per Day</div>
            <div className="calendar-container">
              <Calendar onChange={handleDateChange} value={calenderDate} />
            </div>
          </div>

          <div className="overview-section">
            <div className="GoalsOverviewHeader">
              Overview for{" "}
              {formatDate(
                Array.isArray(calenderDate) ? calenderDate[0] : calenderDate
              )}
            </div>
            <div className="StepsAndCalContainer">
              <div className="StepsCircBar">
                <ProgressCircleright
                  achieved={rightMetrics.stepsValue}
                  target={rightMetrics.stepsTarget}
                  colour="#094752"
                  heading="Steps"
                  unit={rightMetrics.stepsUnit}
                  icon={StepsIcon}
                />
              </div>
              <div className="CaloriesCircBar">
                <ProgressCircleright
                  achieved={rightMetrics.caloriesValue}
                  target={rightMetrics.caloriesTarget}
                  colour="#094752"
                  heading="Calories"
                  unit={rightMetrics.caloriesUnit}
                  icon={CaloriesIcon}
                />
              </div>
            </div>
          </div>

          <div className="DistanceLinearContainer">
            <ProgressLinear
              achieved={
                rightMetrics.distanceValue
              }
              target={
                rightMetrics.distanceTarget
              }
              colour="#094752"
              heading="Distance"
              unit={rightMetrics.distanceUnit}
            />
          </div>
        </div>
      </div>
    </>
  );
};
export default MeDashboard;